import { BookOpen } from 'lucide-react';
import { type ReactNode } from 'react';
import { OVERALL_MARK_LABELS, OVERALL_MARK_MAX, OVERALL_MARK_MIN, SCORECARD_COMMENT_MAX, type MatrixGuideLine, type ScorecardDraft } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Textarea } from '../../../components/Input';
import { ToneIcon, toneCardClass } from '../../../components/Tone';
import { useToneArea } from '../../../components/ToneArea';
import { ChoiceGroup, type Choice } from './ChoiceGroup';
import { MatrixGuideList } from './MatrixGuideList';
import styles from './Scorecard.module.css';

const MARKS: readonly Choice<string>[] = Array.from({ length: OVERALL_MARK_MAX - OVERALL_MARK_MIN + 1 }, (_, index) => {
  const mark = OVERALL_MARK_MIN + index;
  return { value: String(mark), label: String(mark), title: `${mark}: ${OVERALL_MARK_LABELS[mark] ?? ''}` };
});

interface ScorecardFormProps {
  readonly draft: ScorecardDraft;
  readonly onChange: (next: ScorecardDraft) => void;
  readonly isReadOnly: boolean;
  /** An observer (kept from before C5) writes a comment; their mark is optional and never counts. */
  readonly isObserver: boolean;
  /** After a Submit press, what is still missing, so the panelist sees where. */
  readonly isMarkMissing: boolean;
  readonly isCommentMissing: boolean;
  /** INT-02: the round's interview questions, when it has a kit. */
  readonly kitQuestionCount?: number;
  readonly onOpenKit?: () => void;
  /** The job's scoring matrix, as what to look for (owner 2026-11-01). */
  readonly guide?: readonly MatrixGuideLine[];
}

/**
 * The right column of INT-01, HR's correction form on INT-03, and INT-04 by link — C5 (ADR 0077,
 * DECISIONS-LOG 2026-11-01 Q3): one overall mark from 1 to 5 and a short comment. The round weighs
 * the panel's marks into its result. Large targets for a phone or a tablet in a meeting room (D9).
 */
export function ScorecardForm({ draft, onChange, isReadOnly, isObserver, isMarkMissing, isCommentMissing, kitQuestionCount = 0, onOpenKit, guide = [] }: ScorecardFormProps): ReactNode {
  const area = useToneArea();
  const meaning = draft.overallMark === null ? null : (OVERALL_MARK_LABELS[draft.overallMark] ?? null);
  return (
    <div className={styles.form}>
      <MatrixGuideList lines={guide} />
      <section className={`${styles.overall} ${toneCardClass()}`} data-tone={area.tone} aria-labelledby="overall-mark">
        <div className={styles.lineHead}>
          <ToneIcon tone={area.tone} icon={area.icon} />
          <h3 id="overall-mark" className={styles.lineTitle}>
            Overall mark{isObserver ? ' (optional — yours does not count)' : ''}
          </h3>
          {kitQuestionCount > 0 && onOpenKit && (
            <Button size="sm" variant="ghost" icon={BookOpen} onClick={onOpenKit}>
              {kitQuestionCount} {kitQuestionCount === 1 ? 'question' : 'questions'}
            </Button>
          )}
        </div>
        <p className={styles.quiet}>
          From {OVERALL_MARK_MIN} ({OVERALL_MARK_LABELS[OVERALL_MARK_MIN]}) to {OVERALL_MARK_MAX} ({OVERALL_MARK_LABELS[OVERALL_MARK_MAX]}).
        </p>
        <ChoiceGroup
          label="Overall mark"
          choices={MARKS}
          value={draft.overallMark === null ? null : String(draft.overallMark)}
          disabled={isReadOnly}
          isInvalid={isMarkMissing}
          isWide
          onChange={(value) => onChange({ ...draft, overallMark: value === null ? null : Number(value) })}
        />
        <p className={styles.markMeaning} aria-live="polite">
          {meaning ?? '—'}
        </p>
      </section>

      <section className={`${styles.overall} ${toneCardClass()}`} data-tone={area.tone} aria-labelledby="overall-comment">
        <div className={styles.lineHead}>
          <ToneIcon tone={area.tone} icon={area.icon} />
          <h3 id="overall-comment" className={styles.lineTitle}>
            Comment
          </h3>
        </div>
        <p className={styles.quiet}>A few words on why. Later rounds read it, so say what they should probe.</p>
        <Textarea
          aria-labelledby="overall-comment"
          value={draft.notes}
          maxLength={SCORECARD_COMMENT_MAX}
          rows={4}
          disabled={isReadOnly}
          isInvalid={isCommentMissing}
          placeholder="e.g. Strong on IFRS and month-end; vague on the ERP migration — worth probing next round."
          onChange={(event) => onChange({ ...draft, notes: event.target.value })}
        />
      </section>
    </div>
  );
}
